import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BACKEND_URL, getAuthCheckUrl } from "./backendConfig";

const ACTIVE_ROLE_KEY = "portalActiveRole"; // '' or '__all__' = union of all roles
const ACTIVE_ROLE_EVENT = "portal-active-role";

const readActiveRole = () => {
    try { return localStorage.getItem(ACTIVE_ROLE_KEY) || ""; } catch { return ""; }
};

// Broadcast an active-role change so every useAuth() instance (Header, pages…) stays in sync.
export const setPortalActiveRole = (role) => {
    try { localStorage.setItem(ACTIVE_ROLE_KEY, role || ""); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(ACTIVE_ROLE_EVENT, { detail: role || "" }));
};

/* ONE ANSWER FOR THE WHOLE PAGE (Max, 2026-10-21, with a screen recording: "lag và ko smooth").
   Every screen draws its own <Header/>, and every useAuth() asked /portal-auth for itself — two or three
   requests per navigation, and until they came back the sidebar drew only the entries that need no grant:
   37 rows fell to 2 and grew back on every click. The answer now lives here, once: a component that mounts
   while it is known draws from it at once, and one request per navigation (however many components ask in
   that tick) checks it again in the background. That background check is what still notices an expired
   session — a 401 clears the answer and every mounted useAuth() follows, so ProtectedRoute sends the page
   to /login as before. Signing in and out are full page loads, so one account's answer never meets the next. */
const RECHECK_AFTER_MS = 3000;     // a navigation re-asks; the components of ONE navigation share one request
let authCache = null;              // the last successful /portal-auth body
let authCheckedAt = 0;
let authInflight = null;
const authListeners = new Set();

const fetchAuth = () => {
    if (authInflight) return authInflight;
    authInflight = axios.get(getAuthCheckUrl(), { withCredentials: true })
        .then((res) => {
            if (res.data && res.data.status === "Success") {
                // The same answer keeps the same object, so the screens that hold it are not drawn again for nothing.
                if (!authCache || JSON.stringify(authCache) !== JSON.stringify(res.data)) authCache = res.data;
            } else { authCache = null; }
        })
        /* Only a 401 says the session is gone. A blip on the background check (network, 5xx) keeps the answer
           already on screen: before, a re-check happened only as a screen mounted, and a failed one never
           turned a page that was already open into the login screen. With nothing known yet it is a no. */
        .catch((error) => { if (!authCache || (error.response && error.response.status === 401)) authCache = null; })
        .finally(() => {
            authCheckedAt = Date.now();
            authInflight = null;
            authListeners.forEach((fn) => fn(authCache));
        });
    return authInflight;
};

const fromBody = (d) => ({
    auth: !!d,
    userId: d ? d.userId : "",
    name: d ? d.name : "",
    role: d ? d.role : "",
    roles: d && Array.isArray(d.roles) ? d.roles : [],
    /* Display labels for those keys, from 00_PORTAL_roles.role_name. The KEY stays the identity —
       permissionsByRole is keyed on it, localStorage remembers it, and every actingAs() compares it
       — so this is only ever what a person is shown. */
    roleNames: d && d.roleNames && typeof d.roleNames === "object" ? d.roleNames : {},
    // The given name, for greeting somebody. `name` stays the login name and is what the header
    // shows — that chip answers "who is signed in", which is where a surname earns its place.
    firstName: d ? (d.firstName || d.name || "") : "",
    lastLogin: d ? (d.lastLogin || null) : null,   // the last sign-in — the Christmas shower plays once per sign-in
    /* A partner company's account (DAC VN, Jay 2026-10-17): { orgKey, orgName }, or null for HEVN. The
       server decides it from the email domain and refuses such an account everything but its own track;
       the screen only follows (ProtectedRoute sends it there, the Header draws nothing else). */
    external: d && d.external && typeof d.external === "object" ? d.external : null,
    /* A driver-only account (every role it holds is Driver — Max, 2026-10-19): the Driver's Log is its whole portal.
       The server decides it and refuses that account everything else; the screen only follows. */
    driverOnly: !!(d && d.driverOnly === true),
    unionPermissions: d && d.permissions && typeof d.permissions === "object" ? d.permissions : {},
    permissionsByRole: d && d.permissionsByRole && typeof d.permissionsByRole === "object" ? d.permissionsByRole : {},
    // How this person arranged their sidebar (menuOrder.js), or null for the default.
    menuOrder: d && d.menuOrder && typeof d.menuOrder === "object" ? d.menuOrder : null,
    // Their Google photo, kept from their last Google sign-in (UserAvatar.jsx), or null.
    avatarUrl: d && typeof d.avatarUrl === "string" ? d.avatarUrl : null,
});

/* A saved menu order goes into the answer every screen draws from, so the next screen's sidebar is already in it —
   no round trip, and no screen left showing the old order. */
export const setMenuOrderCache = (order) => {
    if (!authCache) return;
    authCache = { ...authCache, menuOrder: order || null };
    authListeners.forEach((fn) => fn(authCache));
};

const useAuth = () => {
    // Known already (an earlier screen asked) → drawn now, not after a round trip.
    const [state, setState] = useState(() => ({ ...fromBody(authCache), loading: !authCache }));
    const [activeRole, setActiveRoleState] = useState(readActiveRole);
    const {
        auth, loading, userId, name, role, roles, roleNames, firstName, external, driverOnly,
        unionPermissions, permissionsByRole, menuOrder, avatarUrl, lastLogin,
    } = state;

    const shown = useRef(authCache);
    useEffect(() => {
        let live = true;
        const onAnswer = (d) => {
            if (!live || (d === shown.current && d)) return;
            shown.current = d;
            setState({ ...fromBody(d), loading: false });
        };
        authListeners.add(onAnswer);
        if (authInflight || !authCache || Date.now() - authCheckedAt > RECHECK_AFTER_MS) fetchAuth();
        return () => { live = false; authListeners.delete(onAnswer); };
    }, []);

    // Keep the active role in sync across every hook instance + other tabs.
    useEffect(() => {
        const onEvent = (e) => setActiveRoleState(e.detail || "");
        const onStorage = (e) => { if (e.key === ACTIVE_ROLE_KEY) setActiveRoleState(e.newValue || ""); };
        window.addEventListener(ACTIVE_ROLE_EVENT, onEvent);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(ACTIVE_ROLE_EVENT, onEvent);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    // If the stored active role isn't one the user currently holds, fall back to "all roles".
    const effectiveActiveRole = activeRole && activeRole !== "__all__" && roles.includes(activeRole) ? activeRole : "";
    // Effective permissions = the chosen role's grants, or the union across all roles.
    const permissions = effectiveActiveRole ? (permissionsByRole[effectiveActiveRole] || {}) : unionPermissions;
    const functions = Object.keys(permissions);

    const setActiveRole = (r) => { setPortalActiveRole(r); setActiveRoleState(r || ""); };

    const logout = async () => {
        try {
            await axios.get(`${BACKEND_URL}/logout`, { withCredentials: true });
            authCache = null;
            setState((s) => ({ ...s, auth: false }));
            window.location.replace(`/login?v=${Date.now()}`);
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    return {
        auth, loading, userId, name, firstName, role, roles, roleNames, external, driverOnly,
        functions, permissions, menuOrder, avatarUrl, lastLogin,
        activeRole: effectiveActiveRole, setActiveRole,
        logout,
    };
};

export default useAuth;
